export type ToolkitMode = "convert-video" | "convert-audio" | "extract-audio" | "merge" | "inspect";

export interface LocalJob {
  mode: ToolkitMode;
  file: File;
  secondary?: File;
  format: string;
  start?: number;
  end?: number;
}

export interface JobCallbacks {
  onStatus: (label: string) => void;
  onProgress: (ratio: number) => void;
  onLog?: (line: string) => void;
}

export type JobResult =
  | { kind: "file"; blob: Blob; filename: string }
  | { kind: "report"; report: string };

export class FfmpegBusyError extends Error {
  constructor() {
    super("Another media job is already running. Wait for it to finish or cancel it.");
    this.name = "FfmpegBusyError";
  }
}

const CORE_VERSION = "0.12.10";
const CORE_BASE = `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${CORE_VERSION}/dist/esm`;

type FFmpegInstance = import("@ffmpeg/ffmpeg").FFmpeg;
let ffmpeg: FFmpegInstance | null = null;
let loadPromise: Promise<FFmpegInstance> | null = null;
let active = false;
let activeCallbacks: JobCallbacks | null = null;
let logLines: string[] = [];

function rememberLog(line: string): void {
  if (!line) return;
  logLines.push(line);
  if (logLines.length > 600) logLines = logLines.slice(-400);
  activeCallbacks?.onLog?.(line);
}

async function createFfmpeg(callbacks: JobCallbacks): Promise<FFmpegInstance> {
  const [{ FFmpeg }, { toBlobURL }] = await Promise.all([import("@ffmpeg/ffmpeg"), import("@ffmpeg/util")]);
  const instance = new FFmpeg();
  instance.on("log", ({ message }) => rememberLog(message));
  instance.on("progress", ({ progress }) => {
    const ratio = Number.isFinite(progress) ? Math.max(0, Math.min(1, progress)) : 0;
    activeCallbacks?.onProgress(ratio);
  });
  callbacks.onStatus("Loading FFmpeg.wasm…");
  const [coreURL, wasmURL] = await Promise.all([
    toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, "text/javascript"),
    toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, "application/wasm"),
  ]);
  await instance.load({ coreURL, wasmURL });
  return instance;
}

async function ensureFfmpeg(callbacks: JobCallbacks): Promise<FFmpegInstance> {
  if (ffmpeg) return ffmpeg;
  if (!loadPromise) {
    loadPromise = createFfmpeg(callbacks)
      .then((instance) => {
        ffmpeg = instance;
        return instance;
      })
      .catch((error) => {
        loadPromise = null;
        throw error;
      });
  }
  return loadPromise;
}

export function terminateFfmpeg(): void {
  if (ffmpeg) {
    try {
      ffmpeg.terminate();
    } catch {
      // The worker may already be gone.
    }
    ffmpeg = null;
    loadPromise = null;
  }
  active = false;
  activeCallbacks = null;
}

function extensionOf(name: string, fallback: string): string {
  const match = /\.([a-z0-9]{2,5})$/i.exec(name);
  return (match?.[1] ?? fallback).toLowerCase();
}

function safeExt(ext: string): string {
  return /^[a-z0-9]{2,5}$/i.test(ext) ? ext.toLowerCase() : "bin";
}

function mimeFor(format: string): string {
  const map: Record<string, string> = {
    mp4: "video/mp4",
    webm: "video/webm",
    mkv: "video/x-matroska",
    mp3: "audio/mpeg",
    m4a: "audio/mp4",
    wav: "audio/wav",
    ogg: "audio/ogg",
    gif: "image/gif",
  };
  return map[format] ?? "application/octet-stream";
}

function outputName(mode: ToolkitMode, format: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `fetchora-${mode}-${stamp}.${safeExt(format)}`;
}

function buildArgs(job: LocalJob, inputName: string, outputNameValue: string): string[] {
  const args: string[] = ["-y"];
  const start = job.start && job.start > 0 ? job.start : 0;
  const end = job.end && job.end > 0 ? job.end : 0;
  if (start) args.push("-ss", String(start));
  args.push("-i", inputName);
  if (end > start) args.push("-t", String(end - start));

  switch (job.mode) {
    case "convert-video":
      if (job.format === "webm") {
        args.push("-c:v", "libvpx-vp9", "-crf", "34", "-b:v", "0", "-c:a", "libopus", "-b:a", "128k");
      } else {
        args.push("-c:v", "libx264", "-preset", "veryfast", "-crf", "26", "-c:a", "aac", "-b:a", "160k", "-movflags", "+faststart");
      }
      break;
    case "convert-audio":
    case "extract-audio":
      args.push("-vn");
      if (job.format === "mp3") args.push("-c:a", "libmp3lame", "-q:a", "4");
      else if (job.format === "m4a") args.push("-c:a", "aac", "-b:a", "192k");
      else if (job.format === "wav") args.push("-c:a", "pcm_s16le");
      else if (job.format === "ogg") args.push("-c:a", "libvorbis", "-q:a", "5");
      break;
    case "merge":
      args.push("-map", "0:v:0", "-map", "1:a:0", "-c", "copy");
      break;
    case "inspect":
      break;
  }
  if (job.mode !== "inspect") args.push(outputNameValue);
  return args;
}

async function deleteIfPresent(instance: FFmpegInstance, name: string): Promise<void> {
  try {
    await instance.deleteFile(name);
  } catch {
    // Nothing to delete.
  }
}

async function withJob<T>(callbacks: JobCallbacks, operation: () => Promise<T>): Promise<T> {
  if (active) throw new FfmpegBusyError();
  active = true;
  activeCallbacks = callbacks;
  logLines = [];
  try {
    return await operation();
  } finally {
    active = false;
    activeCallbacks = null;
  }
}

export async function runLocalJob(job: LocalJob, callbacks: JobCallbacks): Promise<JobResult> {
  if (!job.file) throw new Error("Choose a local file first.");
  if (job.file.size > 1_500_000_000) throw new Error("This browser tool refuses files larger than 1.5 GB. Use a desktop converter instead.");
  if (job.mode === "merge" && !job.secondary) throw new Error("Choose a separate audio file to merge.");
  if (job.start && job.end && job.end <= job.start) throw new Error("The trim end must be greater than the trim start.");

  return withJob(callbacks, async () => {
    const instance = await ensureFfmpeg(callbacks);
    const inputExt = safeExt(extensionOf(job.file.name, job.mode === "convert-audio" ? "mp3" : "mp4"));
    const inputName = `input-${Date.now()}.${inputExt}`;
    const secondExt = job.secondary ? safeExt(extensionOf(job.secondary.name, "m4a")) : "m4a";
    const secondaryName = `audio-${Date.now()}.${secondExt}`;
    const outFormat = job.mode === "inspect" ? "" : safeExt(job.format || (job.mode === "merge" ? "mkv" : "mp4"));
    const outName = outFormat ? outputName(job.mode, outFormat) : "";

    try {
      callbacks.onStatus("Reading the local file into FFmpeg…");
      callbacks.onProgress(0.02);
      await instance.writeFile(inputName, new Uint8Array(await job.file.arrayBuffer()));
      if (job.secondary) {
        await instance.writeFile(secondaryName, new Uint8Array(await job.secondary.arrayBuffer()));
      }

      if (job.mode === "inspect") {
        callbacks.onStatus("Inspecting streams with FFmpeg…");
        try {
          await instance.exec(["-hide_banner", "-i", inputName]);
        } catch {
          // ffmpeg exits nonzero when no output is requested; its logs are the report.
        }
        const report = logLines
          .filter((line) => /Input #|Duration:|Stream #|Metadata:|major_brand|encoder/i.test(line))
          .join("\n")
          .trim();
        return {
          kind: "report",
          report:
            report ||
            "No stream report was produced. The file may be unsupported by this FFmpeg build. Fetchora will not invent metadata.",
        };
      }

      callbacks.onStatus(job.mode === "merge" ? "Muxing compatible streams…" : "Encoding the output…");
      callbacks.onProgress(0.05);
      const args = buildArgs(job, inputName, outName);
      if (job.mode === "merge") {
        // Replace the first input reference with the merged input pair.
        const withoutFirst = args.filter((arg, index) => !(index === 0 && arg === "-y"));
        args.splice(0, args.length, "-y", "-i", inputName, "-i", secondaryName, ...withoutFirst.filter((arg) => arg !== "-i" && arg !== inputName));
      }
      await instance.exec(args);
      const data = await instance.readFile(outName);
      if (typeof data === "string") throw new Error("FFmpeg returned text instead of a media file.");
      const stable = new Uint8Array(data);
      if (!stable.byteLength) throw new Error("FFmpeg produced an empty output file.");
      const blob = new Blob([stable], { type: mimeFor(outFormat) });
      callbacks.onProgress(1);
      callbacks.onStatus("Output ready.");
      return { kind: "file", blob, filename: outputName(job.mode, outFormat) };
    } catch (error) {
      if (error instanceof Error && /terminate|aborted|worker/i.test(error.message)) {
        throw new Error("The FFmpeg worker stopped. The job was cancelled or the browser ran out of memory.");
      }
      if (error instanceof Error) throw error;
      throw new Error(`The browser media worker failed: ${String(error)}`);
    } finally {
      await deleteIfPresent(instance, inputName);
      await deleteIfPresent(instance, secondaryName);
      if (outName) await deleteIfPresent(instance, outName);
    }
  });
}

async function fetchTrack(
  url: string,
  kind: "video" | "audio",
  maxTotal: number,
  onProgress: (fraction: number) => void,
  declaredExt?: string,
): Promise<{ bytes: Uint8Array; ext: string }> {
  const response = await fetch(url, { credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new Error(`The signed ${kind} track could not be downloaded (HTTP ${response.status}).`);
  const length = Number(response.headers.get("content-length") ?? "0");
  if (length && length > maxTotal) throw new Error(`The ${kind} track is larger than the 300 MB browser-processing limit.`);
  if (!response.body) throw new Error(`The ${kind} track had no response body.`);
  const reader = response.body.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    total += value.byteLength;
    if (total > maxTotal) {
      await reader.cancel();
      throw new Error("The tracks exceed the 300 MB browser-processing limit. Download them separately and use the local toolkit.");
    }
    parts.push(value);
    if (length) onProgress(Math.min(0.99, total / length));
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    bytes.set(part, offset);
    offset += part.byteLength;
  }
  const mime = response.headers.get("content-type") ?? "";
  const extension = mime.includes("webm")
    ? "webm"
    : mime.includes("quicktime")
      ? "mov"
      : mime.includes("matroska")
        ? "mkv"
        : mime.includes("mpeg")
          ? kind === "audio"
            ? "mp3"
            : "mpg"
          : mime.includes("mp4")
            ? kind === "audio"
              ? "m4a"
              : "mp4"
            : safeExt(declaredExt ?? (kind === "audio" ? "webm" : "mp4"));
  return { bytes, ext: extension };
}

export async function mergeRemoteTracks(
  video: { url: string; ext: string; title: string },
  audio: { url: string; ext: string; title: string },
  callbacks: JobCallbacks,
): Promise<{ blob: Blob; filename: string }> {
  return withJob(callbacks, async () => {
    const instance = await ensureFfmpeg(callbacks);
    const maxTotal = 300 * 1024 * 1024;
    callbacks.onStatus("Downloading the selected video track…");
    const videoTrack = await fetchTrack(video.url, "video", maxTotal, (fraction) => callbacks.onProgress(fraction * 0.22), video.ext);
    callbacks.onStatus("Downloading the selected audio track…");
    const audioTrack = await fetchTrack(
      audio.url,
      "audio",
      maxTotal - videoTrack.bytes.byteLength,
      (fraction) => callbacks.onProgress(0.22 + fraction * 0.22),
      audio.ext,
    );
    const videoExt = safeExt(videoTrack.ext);
    const audioExt = safeExt(audioTrack.ext);
    const videoName = `merge-video.${videoExt}`;
    const audioName = `merge-audio.${audioExt}`;
    // Render returns an MP4 video track plus a WebM/Opus audio track. Copy the
    // video stream and encode only the audio to AAC so the result is a real MP4
    // rather than silently changing the container to MKV. Non-MP4 video is kept
    // in MKV, still without re-encoding the video.
    const mp4Video = ["mp4", "m4v", "mov"].includes(videoExt);
    const mp4Audio = ["m4a", "mp4", "aac"].includes(audioExt);
    const format = mp4Video ? "mp4" : "mkv";
    const outName = outputName("merge", format);
    try {
      callbacks.onStatus("Loading tracks into FFmpeg…");
      await instance.writeFile(videoName, videoTrack.bytes);
      await instance.writeFile(audioName, audioTrack.bytes);
      callbacks.onStatus(
        format === "mp4" && !mp4Audio
          ? "Muxing to MP4 and encoding the audio to AAC…"
          : "Muxing the tracks without re-encoding…",
      );
      callbacks.onProgress(0.5);
      await instance.exec([
        "-y",
        "-i",
        videoName,
        "-i",
        audioName,
        "-map",
        "0:v:0",
        "-map",
        "1:a:0",
        "-c:v",
        "copy",
        ...(format === "mp4" && !mp4Audio ? ["-c:a", "aac", "-b:a", "192k"] : ["-c:a", "copy"]),
        outName,
      ]);
      const data = await instance.readFile(outName);
      if (typeof data === "string") throw new Error("FFmpeg returned text instead of a media file.");
      const blob = new Blob([new Uint8Array(data)], { type: mimeFor(format) });
      if (!blob.size) throw new Error("The merged file was empty.");
      callbacks.onProgress(1);
      callbacks.onStatus("Merged file ready.");
      const base = video.title.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[\\/:*?"<>|]/g, "").slice(0, 90).trim() || "fetchora-merged";
      return { blob, filename: `${base}-merged.${format}` };
    } finally {
      await deleteIfPresent(instance, videoName);
      await deleteIfPresent(instance, audioName);
      await deleteIfPresent(instance, outName);
    }
  });
}

/**
 * Download a signed audio stream and encode it to MP3 in the browser. The UI
 * labels this action MP3 only after FFmpeg actually produced MP3 bytes.
 */
export async function transcodeRemoteAudioToMp3(
  audio: { url: string; ext: string; title: string },
  callbacks: JobCallbacks,
): Promise<{ blob: Blob; filename: string }> {
  return withJob(callbacks, async () => {
    const instance = await ensureFfmpeg(callbacks);
    callbacks.onStatus("Downloading the signed audio track…");
    const track = await fetchTrack(audio.url, "audio", 300 * 1024 * 1024, (fraction) => callbacks.onProgress(fraction * 0.35), audio.ext);
    const inputName = `mp3-input.${safeExt(track.ext)}`;
    const outName = outputName("convert-audio", "mp3");
    try {
      callbacks.onStatus("Loading the audio track into FFmpeg…");
      await instance.writeFile(inputName, track.bytes);
      callbacks.onStatus("Encoding MP3 with LAME in your browser…");
      callbacks.onProgress(0.4);
      await instance.exec(["-y", "-i", inputName, "-vn", "-c:a", "libmp3lame", "-q:a", "2", outName]);
      const data = await instance.readFile(outName);
      if (typeof data === "string") throw new Error("FFmpeg returned text instead of an MP3 file.");
      const blob = new Blob([new Uint8Array(data)], { type: "audio/mpeg" });
      if (!blob.size) throw new Error("The MP3 output was empty.");
      callbacks.onProgress(1);
      callbacks.onStatus("MP3 ready.");
      const base = audio.title.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[\\/:*?"<>|]/g, "").slice(0, 90).trim() || "fetchora-audio";
      return { blob, filename: `${base}.mp3` };
    } finally {
      await deleteIfPresent(instance, inputName);
      await deleteIfPresent(instance, outName);
    }
  });
}

export function isFfmpegJobActive(): boolean {
  return active;
}
